import type {
  Map as MlMap,
  LayerSpecification,
  GeoJSONSource,
} from 'maplibre-gl';
import type { Feature, FeatureCollection, Point } from 'geojson';
import type { KingdomCollection, KingdomProperties } from '../api/models';

export const KINGDOMS_SOURCE = 'kingdoms';
export const KINGDOMS_LABEL_SOURCE = 'kingdoms-labels';

// Medieval gold, matching the mobile client's kingdom boundary overlay.
const GOLD = '#C9A227';

// kingdomLabelPoints derives one label-anchor Point per kingdom from the
// interior anchor the API supplies (label_lng/label_lat). Labels render from
// this point source, NOT the polygon source: a symbol layer over a polygon
// repeats the label once per map tile the polygon spans, which shows up as the
// name/ruler duplicating across the kingdom as you zoom in. One point → one
// label. Each point carries the kingdom's full properties — the hover tooltip
// reads its stats straight off the label feature. Pure, so it's unit-tested.
export function kingdomLabelPoints(
  data: KingdomCollection,
): FeatureCollection<Point, KingdomProperties> {
  const features: Feature<Point, KingdomProperties>[] = [];
  for (const f of data.features) {
    const { label_lng: lng, label_lat: lat } = f.properties;
    if (lng === undefined || lat === undefined) continue;
    features.push({
      type: 'Feature',
      geometry: { type: 'Point', coordinates: [lng, lat] },
      properties: f.properties,
    });
  }
  return { type: 'FeatureCollection', features };
}

// kingdomLayerDefs returns the three layers: a translucent gold fill and a solid
// boundary line over the polygon source, and a name + NPC-ruler label over the
// dedicated label-point source. Pure (takes no map) so the set can be
// unit-tested.
export function kingdomLayerDefs(): LayerSpecification[] {
  return [
    {
      id: 'kingdoms-fill',
      type: 'fill',
      source: KINGDOMS_SOURCE,
      paint: { 'fill-color': GOLD, 'fill-opacity': 0.12 },
    },
    {
      id: 'kingdoms-line',
      type: 'line',
      source: KINGDOMS_SOURCE,
      paint: { 'line-color': GOLD, 'line-width': 2 },
    },
    {
      id: 'kingdoms-label',
      type: 'symbol',
      source: KINGDOMS_LABEL_SOURCE,
      layout: {
        'text-field': [
          'format',
          ['get', 'name'],
          {},
          '\n',
          {},
          ['get', 'ruler_name'],
          { 'font-scale': 0.8 },
        ],
        // Must be a fontstack the style's glyphs endpoint serves.
        'text-font': ['Open Sans Semibold'],
        'text-size': 13,
      },
      paint: {
        'text-color': '#4A3B26',
        'text-halo-color': '#EFE6CE',
        'text-halo-width': 1.2,
      },
    },
  ] as LayerSpecification[];
}

// addKingdomsLayer adds (or refreshes) the polygon source, the derived
// label-point source, and their layers.
export function addKingdomsLayer(map: MlMap, data: KingdomCollection): void {
  const labels = kingdomLabelPoints(data);
  const polygons = map.getSource(KINGDOMS_SOURCE) as GeoJSONSource | undefined;
  if (polygons) {
    polygons.setData(data);
    (map.getSource(KINGDOMS_LABEL_SOURCE) as GeoJSONSource).setData(labels);
    return;
  }
  map.addSource(KINGDOMS_SOURCE, { type: 'geojson', data });
  map.addSource(KINGDOMS_LABEL_SOURCE, { type: 'geojson', data: labels });
  for (const layer of kingdomLayerDefs()) {
    map.addLayer(layer);
  }
}
